import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PrintIcon from '@mui/icons-material/Print';
import RefreshIcon from '@mui/icons-material/Refresh';
import {LoadingButton} from '@mui/lab';
import {
  Alert,
  Backdrop,
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  Tooltip,
  useTheme,
} from '@mui/material';
import dayjs from 'dayjs';
import update from 'immutability-helper';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
  Bar,
  BarChart,
  Legend,
  ResponsiveContainer,
  Tooltip as TooltipRecharts,
  XAxis,
  YAxis,
} from 'recharts';

import API, {getMessagesFromApiError} from '../../../api/axios';
import {apiBaseUrl} from '../../../api/urls';
import {usePrint} from '../../../hooks/print';
import {useAppSelector} from '../../../hooks/redux';
import {useRefreshInterval} from '../../../hooks/refreshInterval';
import {DashboardPanelData} from '../../../interfaces/Dashboard';
import {
  MachineOverviewBarReportQuery,
  MachineOverviewBarReportResponse,
} from '../../../interfaces/MachineOverviewBarReport';
import reduxSelectors from '../../../redux/selectors';
import {saveFile} from '../../../utils/file';
import {isPresent} from '../../../utils/type-guards';
import {AutoRefreshSelect} from '../../common/AutoRefreshSelect';
import {useDashboardDrilldown} from '../../dashboards/DashboardContext';
import {usePanelFilter} from '../../dashboards/entities/DashboardEntityContext';
import {DateRangeSelect} from '../../selectors/DateRangeSelect';
import {MachineInputSelectV2} from '../../selectors/MachineInputSelectV2';
import {ShiftSelect} from '../../selectors/ShiftSelect';
import {ZoneSelect} from '../../selectors/ZoneSelect';

interface Props {
  value?: DashboardPanelData;
  onUpdate?: (value: DashboardPanelData) => void;
}

const MachineOverviewReportBar = ({value, onUpdate}: Props) => {
  const {filter: config, setFilter: setConfig} = usePanelFilter();

  /*********/
  /* fetch */
  /*********/

  const [fetchedData, setFetchedData] =
    useState<MachineOverviewBarReportResponse>();
  const [fetchedErrors, setFetchedErrors] = useState<string[]>([]);
  const [fetchedInProgress, setFetchedInProgress] = useState(false);

  const params = useMemo<MachineOverviewBarReportQuery>(
    () => ({
      shift_id: config.params?.shift_id ?? value?.params?.shift_id ?? null,
      zone_id: config.params?.zone_id ?? value?.params?.zone_id ?? null,
      date_start:
        config.params?.date_start ??
        value?.params?.date_start ??
        dayjs().format('YYYY-MM-DD'),
      date_end:
        config.params?.date_end ??
        value?.params?.date_end ??
        dayjs().format('YYYY-MM-DD'),
      input: config.params?.input ?? value?.params?.input ?? {},
    }),
    [config]
  );

  const fetchData = useCallback(
    async (params: MachineOverviewBarReportQuery) => {
      setFetchedInProgress(true);
      setFetchedErrors([]);
      try {
        const endpoint = `${apiBaseUrl}/report/machine-overview-bar`;
        const resp = await API.get<MachineOverviewBarReportResponse>(endpoint, {
          params: {
            ...params,
            input: JSON.stringify(params.input),
          },
        });
        setFetchedData(resp.data);
      } catch (error: any) {
        const messages = getMessagesFromApiError(error, "STRA_c97a0709");
        setFetchedErrors(messages);
      }
      setFetchedInProgress(false);
    },
    [params]
  );

  useEffect(() => {
    if (params) {
      fetchData(params);
      onUpdate?.({...value, chartType: 'bar', params});
    }
  }, [params]);

  /****************/
  /* auto refresh */
  /****************/
  const [refreshInterval, setRefreshInterval] = useRefreshInterval(
    () => fetchData(params),
    value?.refreshInterval
  );

  useEffect(() => {
    onUpdate?.({...value, chartType: 'bar', refreshInterval});
  }, [refreshInterval]);

  /*********/
  /* chart */
  /*********/
  const theme = useTheme();
  const bars = [
    {key: 'warning_times', label: 'Warning', color: 'yellow'},
    {key: 'hazard_times', label: 'Hazard', color: 'red'},
    {key: 'remote_times', label: 'Remote Stop', color: 'purple'},
  ];

  const [hiddenKeys, setHiddenKeys] = useState<string[]>([]);
  const toggleKey = (key: string) => {
    setHiddenKeys(
      hiddenKeys.includes(key)
        ? hiddenKeys.filter((i) => i !== key)
        : [...hiddenKeys, key]
    );
  };

  /*********/
  /* print */
  /*********/
  const chartRef = useRef<HTMLDivElement>();
  const [printChart] = usePrint(chartRef.current);
  const printTheme = useAppSelector(({app}) => app.printTheme);

  /*********/
  /* print */
  /*********/
  const [exportInProgress, setExportInProgress] = useState(false);
  const {shifts, machine_inputs: machineInputs} = useAppSelector(
    reduxSelectors.assets.getAssets
  );

  const submitExport = async (params: MachineOverviewBarReportQuery) => {
    setExportInProgress(true);
    try {
      const endpoint = `${apiBaseUrl}/report/machine-overview-export`;
      const resp = await API.get(endpoint, {
        params: {
          ...params,
          input: JSON.stringify(params.input),
          export_type: 'bar',
        },
        responseType: 'blob',
      });

      const fileNameParts = [
        'reportMachinesOverview',
        'Bar',
        `${params.date_start}__${params.date_end}`,
        shifts.find((i) => i.id === params.shift_id)?.name?.replace(' ', '-'),
        machineInputs
          .map((i) => {
            const v = params.input?.[i.id];
            if (v === 1) {
              return `${i.name.replace(' ', '-')}-is-on`;
            } else if (v === 0) {
              return `${i.name.replace(' ', '-')}-is-off`;
            }
          })
          .filter(isPresent)
          .join('_'),
      ].filter(isPresent);

      const fileName = `${fileNameParts.join('__')}.csv`;

      saveFile(resp.data, fileName);
    } catch (error: any) {
      const messages = getMessagesFromApiError(error, "STRA_669761d0");
      setFetchedErrors(messages);
    }
    setExportInProgress(false);
  };

  const drilldown = useDashboardDrilldown();

  return (
    <Box
      display="flex"
      flexDirection="column"
      height="100%"
      gap={1}
      width="100%"
      overflow="hidden"
    >
      <Backdrop open={fetchedInProgress} sx={{position: 'absolute'}}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <Box display="flex" flexDirection="column">
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="start"
          gap={1}
        >
          <Box display="flex" overflow="auto" py={1} gap={1}>
            <Box minWidth={400}>
              <DateRangeSelect
                value={[
                  dayjs(params.date_start).toDate(),
                  dayjs(params.date_end).toDate(),
                ]}
                size="small"
                onChange={(v) => {
                  setConfig(
                    update(config, {
                      params: {
                        date_start: {
                          $set: v?.[0]
                            ? dayjs(v?.[0]).format('YYYY-MM-DD')
                            : undefined,
                        },
                        date_end: {
                          $set: v?.[1]
                            ? dayjs(v?.[1]).format('YYYY-MM-DD')
                            : undefined,
                        },
                      },
                    })
                  );
                }}
              />
            </Box>

            <ShiftSelect
              value={params?.shift_id}
              size="small"
              nullLabel="All Shifts"
              sx={{minWidth: 200}}
              onChange={(v) => {
                setConfig(
                  update(config, {
                    params: {
                      shift_id: {
                        $set: v,
                      },
                    },
                  })
                );
              }}
            />

            <ZoneSelect
              value={params?.zone_id}
              size="small"
              nullLabel="All Sections"
              sx={{minWidth: 200}}
              onChange={(v) => {
                setConfig(
                  update(config, {
                    params: {
                      zone_id: {
                        $set: v,
                      },
                    },
                  })
                );
              }}
            />

            {/* <MachineInputFilterSelect
              value={config.params?.input}
              size="small"
              fullWidth
              onChange={(v) => {
                onChange?.(
                  update(config, {
                    params: {
                      input: {
                        $set: v,
                      }
                    },
                  })
                )
              }}
            /> */}
            <Box width={250}>
              <MachineInputSelectV2
                value={params?.input}
                label="Inputs"
                size="small"
                onChange={(v) => {
                  setConfig(
                    update(config, {
                      params: {
                        input: {
                          $set: v,
                        },
                      },
                    })
                  );
                }}
              />
            </Box>
          </Box>

          <Box display="flex" py={1}>
            <ButtonGroup size="small" sx={{height: 40}}>
              <Button onClick={() => params && fetchData(params)}>
                <RefreshIcon />
              </Button>

              <AutoRefreshSelect
                value={value?.refreshInterval}
                onChange={setRefreshInterval}
              />

              <Tooltip title="Export to Excel">
                <LoadingButton
                  size="small"
                  loading={exportInProgress}
                  variant="outlined"
                  onClick={() => submitExport(params)}
                >
                  <FileDownloadIcon />
                </LoadingButton>
              </Tooltip>

              <Tooltip title="Print">
                <Button onClick={() => printChart()}>
                  <PrintIcon />
                </Button>
              </Tooltip>
            </ButtonGroup>
          </Box>
        </Box>
      </Box>

      {fetchedErrors.map((error, idx) => (
        <Alert
          key={idx}
          severity="error"
          onClose={() => params && fetchData(params)}
        >
          {error}
        </Alert>
      ))}

      <Box
        ref={chartRef}
        overflow="hidden"
        position="relative"
        display="flex"
        flexDirection="column"
        gap={3}
        alignItems="center"
        justifyContent="center"
        height="100%"
        width="100%"
      >
        <Box display="none" displayPrint="block">
          Zones of interest for Machine Types - {params.date_start} to{' '}
          {params.date_end}
        </Box>

        <Box
          height={printTheme ? '500px' : '100%'}
          width={printTheme ? '700px' : '100%'}
        >
          {fetchedData?.results?.length ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={fetchedData.results}>
                <XAxis dataKey="type_name" />
                <YAxis />
                <TooltipRecharts
                  shared={false}
                  labelStyle={{
                    color: theme.palette.text.secondary,
                  }}
                  contentStyle={{
                    backgroundColor: theme.palette.background.default,
                  }}
                  cursor={false}
                />
                <Legend onClick={(item) => toggleKey(item.dataKey)} />

                {bars.map(({key, label, color}) => (
                  <Bar
                    onClick={(event) => {
                      drilldown?.({
                        code: 'MachineDetailsReport',
                        chartType: 'bar',
                        filter: {...params, type_id: event.type_id},
                      });
                    }}
                    key={key}
                    dataKey={key}
                    name={label}
                    hide={hiddenKeys.includes(key)}
                    label={{
                      position: 'top',
                      fill: theme.palette.text.secondary,
                    }}
                    fill={color}
                    isAnimationActive={!printTheme}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <Box
              display="flex"
              height="100%"
              alignItems="center"
              justifyContent="center"
            >
              {fetchedInProgress ? 'Loading...' : 'No data to display'}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default MachineOverviewReportBar;
